import { useEffect, useRef, useState } from 'react';
import type { DocumentInfo } from '../types/document.js';
import { useAuthFetch } from './useAuthFetch.js';

const SERVER_URL = import.meta.env.VITE_SERVER_URL || '';

/**
 * Loads the document named by `?docId=<id>` in the URL.
 *
 * Two callers, one mechanism:
 *
 * - The **meeting stage**. Teams opens the stage as a brand-new instance of
 *   this app at the manifest URL, with empty state and no idea which document
 *   the meeting is looking at. Live Share follow-me does NOT fill the gap:
 *   verified 2026-08-21 in a real meeting, both the presenter's stage and a
 *   follower's stage sat on the upload prompt. So the sharer puts the id in
 *   the URL and the stage fetches it directly — no dependency on follow
 *   state, auth timing, or Live Share reaching the stage at all.
 * - Two-window sync testing on localhost, which is what this was written for.
 */
export function useDocFromUrl(currentDocumentId: string | undefined | null): {
  urlDocument: DocumentInfo | null;
} {
  const { authFetch, isAuthenticated } = useAuthFetch();
  const [urlDocument, setUrlDocument] = useState<DocumentInfo | null>(null);
  const fetchingRef = useRef<string | null>(null);
  const loadedRef = useRef<string | null>(null);

  useEffect(() => {
    // Wait until auth has issued a token. Firing authFetch with `status ===
    // initializing` throws a noisy "no auth token" warning that hits the
    // console even though the retry-after-auth-ready path succeeds. Gating
    // here keeps the dev console clean. Same stale-closure pattern as
    // CLAUDE.md gotcha #27 — we depend on `authFetch` so the effect re-fires
    // when it changes, but we ALSO gate on `isAuthenticated` so the first
    // run doesn't attempt a fetch with a null token.
    if (!isAuthenticated) return;

    const params = new URLSearchParams(window.location.search);
    const docId = params.get('docId');
    if (!docId || docId === currentDocumentId) return;
    // Don't re-fetch a docId we already successfully loaded — the parent may
    // not have promoted urlDocument to documentInfo yet, but we shouldn't
    // hammer the server (and create a new bytes reference) on every render.
    if (loadedRef.current === docId) return;
    if (fetchingRef.current === docId) return;
    fetchingRef.current = docId;

    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${SERVER_URL}/api/documents/${docId}`);
        if (!res.ok || cancelled) return;
        const result = (await res.json()) as {
          documentId: string;
          fileName: string;
          mimeType: string;
          documentType: 'pdf' | 'image' | 'presentation' | 'document' | 'spreadsheet';
          base64: string;
        };
        if (cancelled) return;

        const binary = atob(result.base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

        loadedRef.current = result.documentId;
        setUrlDocument({
          documentId: result.documentId,
          fileName: result.fileName,
          mimeType: result.mimeType,
          documentType: result.documentType,
          data: bytes,
        });
      } catch (err) {
        console.warn('docId fetch failed:', err);
      } finally {
        // Always clear so a re-fire (e.g. after authFetch becomes ready) can
        // retry. Leaving it pinned would block the second attempt.
        fetchingRef.current = null;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authFetch, currentDocumentId, isAuthenticated]);

  return { urlDocument };
}
